import React, { useMemo } from 'react';
import { Lead, FunnelStage, CrmUser } from '../../../../types';
import { useDealModals } from '../../../../features/kanban/hooks/useDealModals';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useDealCardActions } from '../../hooks/useDealCardActions';
import { EditableField } from '../../../../components/shared/InlineEditable';
import { DealStageSelector } from './DealStageSelector';
import { DealCardOverflowMenu } from './DealCardOverflowMenu';
import { DealCardFooter } from './DealCardFooter';

interface DealCardProps {
    lead: Lead;
    funnelStages: FunnelStage[];
    crmUsers: CrmUser[];
    onStageChange: (leadId: string, stageId: string) => void;
    onLeadUpdate: (lead: Lead) => void;
}

function dealAccentClass(status: Lead['status']): string {
    if (status === 'won') return 'deal-card-accent--won';
    if (status === 'lost') return 'deal-card-accent--danger';
    return 'deal-card-accent--brand';
}

function dealCardModifier(status: Lead['status']): string {
    if (status === 'won') return 'deal-card--won';
    if (status === 'lost') return 'deal-card--lost';
    return '';
}

export const DealCard: React.FC<DealCardProps> = ({
    lead,
    funnelStages,
    crmUsers,
    onStageChange,
    onLeadUpdate
}) => {
    const { canCreateLeads, canDeleteLeads } = usePermissions();
    const { handleToKanban, handleDuplicate, handleDeleteConfirmed } = useDealCardActions({
        lead,
        funnelStages
    });

    const leadsByStage = useMemo(() => ({ [lead.stage]: [lead] }), [lead]);
    const modals = useDealModals(leadsByStage);

    const handleNameSave = (value: string | number) => {
        onLeadUpdate({ ...lead, name: String(value) });
    };

    const handleValueSave = (value: string | number) => {
        onLeadUpdate({ ...lead, value: Number(value) });
    };

    const handleAgentChange = (agentId: string) => {
        onLeadUpdate({ ...lead, agent: agentId || null });
    };

    return (
        <article className={`deal-card deal-card--static mb-2 ${dealCardModifier(lead.status)}`}>
            <div className={`deal-card-accent ${dealAccentClass(lead.status)}`} aria-hidden="true" />

            <div className="flex min-w-0 items-start gap-2">
                <div className="min-w-0 flex-1 space-y-2">
                    <EditableField
                        label="Nome"
                        value={lead.name}
                        type="text"
                        onSave={handleNameSave}
                    />
                </div>
                <DealCardOverflowMenu
                    leadName={lead.name}
                    canCreateLeads={canCreateLeads}
                    canDeleteLeads={canDeleteLeads}
                    onGoToKanban={() => void handleToKanban()}
                    onDuplicate={() => void handleDuplicate()}
                    onDeleteConfirmed={handleDeleteConfirmed}
                />
            </div>

            <DealStageSelector
                currentStage={lead.stage}
                status={lead.status as 'active' | 'won' | 'lost'}
                stages={funnelStages}
                onStageChange={(stageId) => onStageChange(lead.id, stageId)}
                onWin={() => modals.handleFinalStageDrop(lead.id, 'won')}
                onLose={() => modals.handleFinalStageDrop(lead.id, 'lost')}
            />

            <DealCardFooter
                lead={lead}
                crmUsers={crmUsers}
                onValueSave={handleValueSave}
                onAgentChange={handleAgentChange}
                winDealModal={modals.winDealModal}
                lossReasonModal={modals.lossReasonModal}
                closeWinModal={modals.closeWinModal}
                closeLossModal={modals.closeLossModal}
                handleConfirmWin={modals.handleConfirmWin}
                handleConfirmLoss={modals.handleConfirmLoss}
            />
        </article>
    );
};

export default DealCard;
